import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Drawer from "@material-ui/core/Drawer";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Container from "@material-ui/core/Container";
import MenuIcon from "@material-ui/icons/Menu";
import { Route, Switch } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { SideBarMenuItems } from "../components/sidebar/SideBarMenuItems";
import { screenRoutes } from "../routers/ScreensRoutes";
import { IconArrowBarToLeft } from "@tabler/icons-react";
import logo from "../assets/logo_full.png";
import ProfileActionsButtons from "../components/ProfileActionsButton";
import { Box } from "@material-ui/core";
import NotFound from "../routers/NotFound";
import { setDrawerOpen } from "../slices/commonSlice";

// const Copyright = () => {
//   return (
//     <Typography variant="body2" color="textSecondary" align="center">
//       {"Copyright © "}
//       <Link color="inherit" href="https://material-ui.com/">
//         BlackBox EC
//       </Link>{" "}
//       {new Date().getFullYear()}
//       {"."}
//     </Typography>
//   );
// };

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
    zIndex: 0,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    position: "relative",
    color: theme.palette.primary.main,
    ...theme.mixins.toolbar,
  },
  appBar: {
    borderBottom: "1px solid gainsboro",
    backgroundColor: `rgba(255,255,255,.64)`,
    backdropFilter: `blur(8px)`,
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.primary.main,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    borderRight: "1px solid gainsboro",
    width: drawerWidth,
    padding: "0px 10px",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    border: 0,
  },
  drawerPaperClose: {
    overflowX: "auto",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(8),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(8),
      "& .MuiListItemText-root": {
        opacity: 0,
      },
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing(0),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 60 - drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    paddingRight: 10,
    paddingLeft: 10,
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  fixedHeight: {
    height: 240,
  },
}));

const Dashboard = () => {
  const classes = useStyles();

  const { userInfo } = useAppSelector((state) => state.auth);
  const { drawerOpen } = useAppSelector((state) => state.common);
  const dispatch = useAppDispatch();

  // const [open, setOpen] = React.useState(true);
  const handleDrawerOpen = () => {
    dispatch(setDrawerOpen(true));
    // setOpen(true);
  };
  const handleDrawerClose = () => {
    dispatch(setDrawerOpen(false));
    // setOpen(false);
  };

  return (
    <Box style={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        elevation={0}
        position="fixed"
        component="nav"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            <div style={{ display: "flex" }}>
              <img src={logo} alt="logo" height={50} />
            </div>
          </Typography>
          <ProfileActionsButtons />
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        style={{
          width: drawerWidth,
          flexShrink: 0,
        }}
        open={drawerOpen}
        onClose={handleDrawerClose}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleDrawerClose}>
            <IconArrowBarToLeft />
          </IconButton>
        </div>
        <SideBarMenuItems />
      </Drawer>

      <Box
        component="main"
        className={clsx(classes.content, {
          [classes.contentShift]: drawerOpen,
        })}
        style={{ flexGrow: 1, overflow: "auto" }}
      >
        <Toolbar />
        <Container maxWidth="xl" className={classes.container}>
          <Switch>
            {screenRoutes
              .filter((screen) => screen.roles.includes(userInfo?.perfil!))
              .map((item) => {
                if (item.children) {
                  return item.children.map((child) => (
                    <Route path={child.path} key={JSON.stringify(child)}>
                      {child.screen}
                    </Route>
                  ));
                }
                return (
                  <Route path={item.path} key={JSON.stringify(item)}>
                    {item.screen}
                  </Route>
                );
              })}
            <Route path="*">
              <NotFound />
            </Route>
          </Switch>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
